import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Mail, CheckCircle, Loader2, Sparkles, Building2, User, Heart, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/api';


export function NewsletterForm({ variant = 'default', className = '' }) {
  var [email, setEmail] = useState('');
  var [name, setName] = useState('');
  var [subscriberType, setSubscriberType] = useState('');  // 'persona' or 'profesional'
  var [loading, setLoading] = useState(false);
  var [subscribed, setSubscribed] = useState(false);
  var [showNameField, setShowNameField] = useState(false);
  var [step, setStep] = useState(1);  // 1: type selection, 2: email

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!email) {
      toast.error('Por favor ingresa tu email');
      return;
    }
    
    if (!subscriberType) {
      toast.error('Por favor selecciona una opción');
      return;
    }
    
    setLoading(true);
    try {
      var res = await axios.post(API + '/newsletter/subscribe', {
        email: email,
        name: name || null,
        subscriber_type: subscriberType,
        interests: ['all']
      });
      setSubscribed(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al suscribirse');
    }
    setLoading(false);
  }

  if (subscribed) {
    return (
      <div className={'text-center py-6 ' + className}>
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-xl font-semibold text-slate-50 mb-2">¡Gracias por suscribirte!</h3>
        <p className="text-slate-400">Revisa tu email para ver los recursos que tenemos para ti.</p>
      </div>
    );
  }

  // Compact inline variant with type selector
  if (variant === 'inline') {
    return (
      <div className={className}>
        {/* Type selector */}
        <div className="flex gap-2 mb-3 justify-center">
          <button
            type="button"
            onClick={function() { setSubscriberType('persona'); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm ${
              subscriberType === 'persona' 
                ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' 
                : 'border-slate-700 text-slate-400 hover:border-slate-600'
            }`}
          >
            <Heart className="w-4 h-4" />
            Busco ayuda
          </button>
          <button
            type="button"
            onClick={function() { setSubscriberType('profesional'); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm ${
              subscriberType === 'profesional' 
                ? 'border-violet-500 bg-violet-500/20 text-violet-400' 
                : 'border-slate-700 text-slate-400 hover:border-slate-600'
            }`}
          >
            <Briefcase className="w-4 h-4" />
            Soy profesional
          </button>
        </div>
        {/* Email form */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            type="email"
            placeholder="Tu email"
            value={email}
            onChange={function(e) { setEmail(e.target.value); }}
            className="bg-slate-800 border-slate-700 flex-1"
            required
          />
          <Button 
            type="submit" 
            disabled={loading || !subscriberType} 
            className={subscriberType === 'profesional' ? 'bg-violet-500 hover:bg-violet-600 whitespace-nowrap' : 'bg-emerald-500 hover:bg-emerald-600 whitespace-nowrap'}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suscribirse'}
          </Button>
        </form>
      </div>
    );
  }

  // Footer variant with type selector
  if (variant === 'footer') {
    return (
      <div className={className}>
        <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Mail className="w-5 h-5 text-emerald-400" />
          Newsletter
        </h4>
        <p className="text-sm text-slate-400 mb-4">
          Recibe información sobre centros, artículos y recursos gratuitos.
        </p>
        {/* Type selector */}
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={function() { setSubscriberType('persona'); }}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm ${
              subscriberType === 'persona' 
                ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' 
                : 'border-slate-700 text-slate-400 hover:border-slate-600'
            }`}
          >
            <Heart className="w-4 h-4" />
            Busco ayuda
          </button>
          <button
            type="button"
            onClick={function() { setSubscriberType('profesional'); }}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm ${
              subscriberType === 'profesional' 
                ? 'border-violet-500 bg-violet-500/20 text-violet-400' 
                : 'border-slate-700 text-slate-400 hover:border-slate-600'
            }`}
          >
            <Briefcase className="w-4 h-4" />
            Profesional
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-2">
          <Input
            type="email"
            placeholder="tu@email.com"
            value={email}
            onChange={function(e) { setEmail(e.target.value); }}
            className="bg-slate-800 border-slate-700"
            required
          />
          <Button 
            type="submit" 
            disabled={loading || !subscriberType} 
            className={`w-full ${subscriberType === 'profesional' ? 'bg-violet-500 hover:bg-violet-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Suscribirse
          </Button>
        </form>
      </div>
    );
  }

  // Default card variant with type selector
  return (
    <Card className={'bg-gradient-to-br from-slate-900 to-slate-800 border-0 overflow-hidden ' + className}>
      <CardContent className="p-8 relative">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-teal-500/10 rounded-full blur-2xl"></div>
        
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-50">Mantente Informado</h3>
              <p className="text-sm text-slate-400">Únete a nuestra comunidad</p>
            </div>
          </div>
          
          <p className="text-slate-400 mb-6">
            Recibe actualizaciones semanales sobre nuevos centros, artículos sobre adicciones, 
            recursos gratuitos y herramientas de autoayuda.
          </p>
          
          {/* Type selector */}
          <div className="flex gap-3 mb-4">
            <button
              type="button"
              onClick={function() { setSubscriberType('persona'); }}
              className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                subscriberType === 'persona' 
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                  : 'border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              <Heart className={`w-6 h-6 ${subscriberType === 'persona' ? 'text-emerald-400' : ''}`} />
              <span className="text-sm font-medium">Busco ayuda</span>
              <span className="text-xs text-slate-500">Para mí o un familiar</span>
            </button>
            <button
              type="button"
              onClick={function() { setSubscriberType('profesional'); }}
              className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                subscriberType === 'profesional' 
                  ? 'border-violet-500 bg-violet-500/10 text-violet-400' 
                  : 'border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              <Briefcase className={`w-6 h-6 ${subscriberType === 'profesional' ? 'text-violet-400' : ''}`} />
              <span className="text-sm font-medium">Soy profesional</span>
              <span className="text-xs text-slate-500">Centro o profesional de salud</span>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={function(e) { setEmail(e.target.value); }}
              className="bg-slate-800/50 border-slate-700 h-12"
              required
            />
            
            {showNameField && (
              <Input
                type="text"
                placeholder="Tu nombre (opcional)"
                value={name}
                onChange={function(e) { setName(e.target.value); }}
                className="bg-slate-800/50 border-slate-700"
              />
            )}
            
            <Button 
              type="submit" 
              disabled={loading || !subscriberType} 
              className={`w-full h-12 text-base ${subscriberType === 'profesional' ? 'bg-violet-500 hover:bg-violet-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Suscribiendo...</>
              ) : (
                <><Mail className="w-5 h-5 mr-2" />Suscribirme Gratis</>
              )}
            </Button>
            
            {!showNameField && (
              <button
                type="button"
                onClick={function() { setShowNameField(true); }}
                className="text-sm text-slate-500 hover:text-slate-400 w-full"
              >
                + Agregar mi nombre
              </button>
            )}
          </form>
          
          <p className="text-xs text-slate-500 mt-4 text-center">
            Sin spam. Puedes cancelar cuando quieras.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default NewsletterForm;
